import React, { useEffect, useRef } from 'react';
import { useGoogleApi } from '@goodfynd/context';
import { Button, Image, Loading } from '@goodfynd/ui';
import type { VendorEventMapMethods } from '@goodfynd/maps';
import { VendorEventMapCanvas } from '@goodfynd/maps';
import { useRouter } from 'next/router';

import strings from '../../config/strings';
import { ScheduleListEmbedSearch } from '../ScheduleListEmbed/ScheduleListEmbedSearch';

import { ScheduleMapCard, ScheduleMapPinHover } from './ScheduleMapCard';
import { StyledEmbedContainer, StyledLoadMoreContainer } from './styles';
import type { ScheduleMapEmbedProps } from './types';
import { useScheduleEmbed } from './useScheduleEmbed';

export function ScheduleMapEmbed({
  canvasCss,
  className,
  getHref,
  height,
  markerHeight = 64,
  markerImage,
  markerWidth = 64,
  selectZoom = 19,
  zoom = 15,
  scheduleOptions,
}: ScheduleMapEmbedProps) {
  const { isReady } = useRouter();

  const { isLoaded: googleApiIsLoaded } = useGoogleApi();

  const mapRef = useRef<VendorEventMapMethods>(null);

  const {
    busy,
    center,
    dayOptions,
    fetchNextPage,
    geoSearch,
    hasFilters,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading,
    isMobile,
    location,
    results,
    selectedDate,
    setSelectedDate,
    total,
  } = useScheduleEmbed({
    ...scheduleOptions,
    isReady,
    selectZoom: scheduleOptions?.selectZoom ?? selectZoom,
  });

  useEffect(() => {
    if (location === undefined) {
      return;
    }

    const lat = Number(location.latitude);
    const lng = Number(location.longitude);
    mapRef.current?.panTo?.({ lat, lng });
    mapRef.current?.setZoom?.(Number(zoom));
  }, [location, zoom]);

  if (!googleApiIsLoaded || isReady !== true || center?.longitude === 0) {
    return <Loading />;
  }

  return (
    <StyledEmbedContainer className={className}>
      <VendorEventMapCanvas
        busy={isLoading && !results[0] && 'loading map'}
        defaultCenter={center}
        defaultZoom={Number(zoom)}
        error={isError && 'An unknown error has occured'}
        onSelectZoom={selectZoom}
        ref={mapRef}
        renderMarkerHoverInfo={(info) => <ScheduleMapPinHover item={info} />}
        renderMarkerInfo={(info) => <ScheduleMapPinHover item={info} />}
        results={results}
        css={{
          height,
          ...canvasCss,
        }}
        marker={{
          custom:
            typeof markerImage === 'string' ? (
              <Image
                alt="Marker icon"
                height={markerHeight}
                src={markerImage}
                unoptimized
                width={markerWidth}
              />
            ) : undefined,
        }}
        renderListFooter={() =>
          hasNextPage && Number(total) > 0 ? (
            <StyledLoadMoreContainer>
              <Button
                align="center"
                busy={isFetchingNextPage}
                disabled={isFetchingNextPage}
                onClick={() => fetchNextPage()}
                size="small"
              >
                {strings.labels.loadMore}
              </Button>
            </StyledLoadMoreContainer>
          ) : (
            <></>
          )
        }
        renderListHeader={
          hasFilters && !busy
            ? () => (
                <ScheduleListEmbedSearch
                  dayOptions={dayOptions}
                  forceMobile
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  geoSearch={geoSearch.renderInput(
                    'default',
                    isMobile ? 'input-top' : 'input-left'
                  )}
                />
              )
            : undefined
        }
        renderListItem={({ index = -1, point: { properties: item } }) => (
          <ScheduleMapCard
            href={getHref?.(item)}
            item={item}
            number={index > -1 ? index + 1 : 0}
          />
        )}
        sidebarCss={{
          li: {
            height: 'fit-content !important',
          },
          '@mdMax': {
            height: '13.875rem',
          },
        }}
      />
    </StyledEmbedContainer>
  );
}
